<template>
    <div
        class="fixed text-sm bg-gray-900 text-white shadow-md p-4 rounded"
        style="z-index: 1000"
        :class="{
            hidden: !show,
            'top-5 right-5 max-w-sm': device === 'ipad',
            'bottom-5 right-5 left-5': device !== 'ipad',
        }"
    >
        <div
            class="bg-gray-900 absolute transform rotate-45 w-4 h-4 prompt-carat"
            :class="{
                'translate-x-4 -translate-y-1/2 right-1/3 top-0':
                    device === 'ipad',
                '-translate-x-1/2 translate-y-1/2 left-1/2 bottom-0':
                    device !== 'ipad',
            }"
        />
        <a class="flex" href="#" @click.prevent.stop="theyNeedMoreHelp">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                class="w-6 h-6 flex-shrink-0 mr-3 mt-2 text-white"
            >
                <line
                    class="stroke-current ios-share-icon-inner"
                    x1="12"
                    y1="13.499"
                    x2="12"
                    y2="0.499"
                />
                <polyline
                    class="stroke-current ios-share-icon-inner"
                    points="7.5 4.999 12 0.499 16.5 4.999"
                />
                <path
                    class="stroke-current ios-share-icon-inner"
                    d="M15,8.5h4a1,1,0,0,1,1,1v13a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1V9.5a1,1,0,0,1,1-1H9"
                />
            </svg>
            <div>
                <p class="mb-1 font-bold">
                    You can install this app to your phone!
                </p>
                <p class="mb-1">
                    Tap the
                    <strong>Share</strong>
                    button {{ device === 'ipad' ? 'above' : 'below' }} and press
                    <strong>Add to Home Screen</strong>.
                </p>
                <p></p>
                <p>
                    <a
                        class="text-gray-200 text-xs"
                        @click.prevent.stop="hide"
                        href="#"
                        >Dismiss</a
                    >
                </p>
            </div>
        </a>

        <portal>
            <modal @close="showExtraHelp = false" v-if="showExtraHelp">
                <p
                    class="mt-1 mb-3 text-3xl text-center tracking-tight leading-10 font-extrabold text-gray-900 sm:leading-none sm:text-5xl lg:text-4xl xl:text-5xl"
                >
                    Add Rat T to Your Home Screen
                </p>
                <p class="mb-4">
                    Press the share button (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        class="w-5 h-5 -ml-1 -mr-1 transform -translate-y-1 text-black inline-block"
                    >
                        <line
                            class="stroke-current ios-share-icon-inner"
                            x1="12"
                            y1="13.499"
                            x2="12"
                            y2="0.499"
                        />
                        <polyline
                            class="stroke-current ios-share-icon-inner"
                            points="7.5 4.999 12 0.499 16.5 4.999"
                        />
                        <path
                            class="stroke-current ios-share-icon-inner"
                            d="M15,8.5h4a1,1,0,0,1,1,1v13a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1V9.5a1,1,0,0,1,1-1H9"
                        />
                    </svg>
                    ) at the {{ device === 'ipad' ? 'top right' : 'bottom' }} of
                    your browser and press
                    <strong>Add to Home Screen</strong> from the list that pops
                    up.
                </p>

                <p class="text-center mb-4">It looks like this:</p>

                <div class="w-56 mx-auto" style="--aspect-ratio:389*359">
                    <img
                        src="https://res.cloudinary.com/dlgnpky2q/image/upload/f_auto/v1591233681/rat-t/add-to-home-help.png"
                        alt="Add to Home"
                    />
                </div>
            </modal>
        </portal>
    </div>
</template>

<script>
import { timing } from '@/util/animation';
import { Portal } from '@linusborg/vue-simple-portal';
import Modal from '@/components/Modal';

export default {
    data() {
        return {
            showExtraHelp: false,
            show: false,
        };
    },
    components: {
        Portal,
        Modal,
    },
    mounted() {
        this.display();
    },
    computed: {
        device() {
            const agent = window.navigator.userAgent.toLowerCase();

            if (/iphone|ipod/.test(agent)) {
                return 'iphone';
            }

            if (
                /ipad/.test(agent) ||
                (/macintosh/.test(agent) &&
                    navigator.maxTouchPoints &&
                    navigator.maxTouchPoints > 2)
            ) {
                return 'ipad';
            }

            return false;
        },
        isInStandaloneMode() {
            return (
                'standalone' in window.navigator && window.navigator.standalone
            );
        },
        shouldDisplay() {
            const visits = parseInt(
                window.localStorage.getItem('visits') || 0,
                10,
            );

            window.localStorage.setItem('visits', visits + 1);

            if (visits === 0) {
                // Let's not be annoying, let them look around at
                // least once before nagging them to install
                return false;
            }

            if (visits > 5) {
                // Let's not be annoying, again.
                // If they've ignored this over 5 times, stop prompting them
                return false;
            }

            // If it's on iOS and they don't have it installed, hit it
            return this.device && !this.isInStandaloneMode;
        },
    },
    methods: {
        hide() {
            return new Promise(resolve => {
                gsap.to(this.$el, {
                    opacity: 0,
                    y: '-=10',
                    duration: 0.25,
                    onComplete: () => {
                        this.show = false;
                        resolve();
                    },
                });
            });
        },
        display() {
            if (!this.shouldDisplay) {
                return;
            }

            setTimeout(() => {
                this.show = true;

                gsap.from(this.$el, {
                    opacity: 0,
                    y: '-=10',
                    duration: 0.7,
                });
            }, timing(5000));
        },
        theyNeedMoreHelp() {
            this.hide().then(() => {
                this.showExtraHelp = true;
            });
        },
    },
};
</script>

<style></style>
